"use client";

import { useState } from "react";
import {
  Grid,
  GridContent,
  GridItem,
  GridLoadMore,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { Section } from "@/components/Section/Section";
import { Headline } from "@/components/Headline/Headline";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { Modul_Rohstoffübersicht } from "@/wp/types/acf-module-types";
import { removeExtraSpacesFromClassNames } from "@/utils/removeExtraSpacesFromClassNames";
import { MaterialOverviewDataFetched } from "@/getData/getIndexedMaterialOverviewData";
import { useAnimeKeyWithDep } from "@/hooks/useAnimeKey";
import { FileType } from "@/types/baseTypes";
import { MaterialCard } from "./MaterialCard";
import buttonStyles from "../../components/Button/Button.module.scss";
import styles from "./MaterialOverview.module.scss";
import { OptionsFactsheetsType } from "@/types/OptionType";

export type MaterialOverviewModuleType = Modul_Rohstoffübersicht;

interface MaterialOverviewProps {
  data: MaterialOverviewModuleType;
  materialOverviewData: MaterialOverviewDataFetched | undefined;
  metalCsvFile: FileType;
  factsheets: OptionsFactsheetsType;
}

const MaterialOverview = ({
  data,
  materialOverviewData,
  metalCsvFile,
  factsheets,
}: MaterialOverviewProps) => {
  const {
    id,
    background_color_class,
    section_padding_class,
    section_margin_class,
    headline_text_align_class,
    // posts_per_page,
  } = data.layout;

  const { overline, headline_html_tag, headline, subline } = data.metal_grid;

  const { getLabel } = useGlobalContext();
  const [activeCategory, setActiveCategory] = useState<number | null>(null);

  const refreshKey = useAnimeKeyWithDep({
    ext: "refresh",
    deps: [activeCategory],
  });

  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  const metals = materialOverviewData?.metals || [];
  const categories = materialOverviewData?.categories || [];

  const filteredMetals = activeCategory
    ? metals.filter((m) => m.categoryIds.includes(activeCategory))
    : metals;

  const className = removeExtraSpacesFromClassNames(
    `${styles["material-overview"]} ${background_color_class || ""} ${section_margin_class || ""} ${section_padding_class || ""}`,
  );

  const listId = id ? `${id}-material-overview-list` : "material-overview-list";

  const headingLevel = headline_html_tag === "h3" ? 4 : 3;

  return (
    <Section className={className} columnsClassName="is-centered" id={id}>
      <div className="column is-12">
        {(headline || overline || subline) && (
          <Headline
            headline_html_tag={headline_html_tag}
            headline={headline}
            overline={overline}
            subline={subline}
            extraClass={headline_text_align_class}
          />
        )}

        {categories.length > 1 && (
          <div
            role="group"
            aria-controls={listId}
            aria-label={getLabel(
              "material_overview_filter_aria_label",
              "Filter Optionen",
            )}
            className={styles["filter-bar"]}
          >
            <button
              className={removeExtraSpacesFromClassNames(
                `${styles["filter-btn"]} ${activeCategory === null ? styles["is-active"] : ""}`,
              )}
              aria-expanded={activeCategory === null}
              onClick={() => setActiveCategory(null)}
            >
              {getLabel("material_overview_all_products", "Alle Produkte")}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={removeExtraSpacesFromClassNames(
                  `${styles["filter-btn"]} ${activeCategory === cat.id ? styles["is-active"] : ""}`,
                )}
                aria-expanded={activeCategory === cat.id}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}

        <div id={listId} aria-live="polite" aria-atomic="false">
          <Grid
            key={String(activeCategory)}
            postsPerPage={filteredMetals.length}
            refreshKey={refreshKey}
            initialItems={filteredMetals}
            initialTotalPosts={filteredMetals.length}
            labels={{ loading: "" }}
            fetchItems={({ postsPerPage, offset }) => {
              const sliced = filteredMetals.slice(
                offset,
                offset + postsPerPage,
              );
              return {
                posts: sliced,
                totalPosts: filteredMetals.length,
              };
            }}
          >
            {(gridMetals) => (
              <>
                <GridContent gap="1.5rem">
                  {gridMetals.map((metal, idx) => (
                    <GridItem
                      key={metal.id}
                      itemWidth={{
                        mobile: "100%",
                      }}
                    >
                      <MaterialCard
                        element={metal}
                        metalCsvFile={metalCsvFile}
                        headingLevel={headingLevel}
                        index={idx}
                        factsheets={factsheets}
                      />
                    </GridItem>
                  ))}
                </GridContent>
                <GridLoadMore
                  grid_navigation="only-pagination"
                  buttonStyles={buttonStyles}
                />
              </>
            )}
          </Grid>
        </div>
      </div>
    </Section>
  );
};

export default MaterialOverview;
